import { Alert, Button, Modal } from 'antd'
import { FieldValueLookupProvider } from 'features/libra/fieldValues/FieldValueLookupContext'
import { useWorkflowFieldValues } from 'features/libra/fieldValues/useWorkflowFieldValues'
import {
  InputField,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { useOntologyFields } from 'features/libra/ontology/useOntologyFields'
import { getFlowchartFieldId } from 'features/libra/sections/flowchartSection/flowchartTypes'
import { Section, SectionType } from 'features/libra/sections/sectionTypes'
import {
  LibraWorflowStepComponentProps,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import React, { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import cantileverTheme from 'styles/cantileverTheme'

import {
  ConditionalFormConfig,
  ConditionalFormConfigSchema,
} from './conditionalForm/api/conditionalFormApi'
import { ConditionalFormSectionView } from './conditionalForm/conditionalFormSection/ConditionalFormSection'
import { isConditionalFormStepComplete } from './conditionalForm/conditionalFormStepCompletion'
import { FlowchartSectionView } from './conditionalForm/flowchart/FlowchartSection'

interface SectionRendererProps {
  section: Section
  ontologyFields: Record<string, OntologyField>
  fieldValuesWithDefaults: Record<string, unknown>
  initialFieldValues: Record<string, unknown>
  setFieldValues: (
    updates:
      | Record<string, unknown>
      | ((prev: Record<string, unknown>) => Record<string, unknown>)
  ) => void
}

const SectionRenderer: FC<SectionRendererProps> = ({
  section,
  ontologyFields,
  fieldValuesWithDefaults,
  initialFieldValues,
  setFieldValues,
}) => {
  switch (section.type) {
    case SectionType.FLOWCHART: {
      const fieldId = getFlowchartFieldId(section)
      const fieldDef = ontologyFields[fieldId] as InputField | undefined

      return (
        <FlowchartSectionView
          section={section}
          fieldId={fieldId}
          fieldDef={fieldDef}
          ontologyFields={ontologyFields}
          fieldValue={fieldValuesWithDefaults[fieldId]}
          savedFieldValue={initialFieldValues[fieldId]}
          detailsValue={
            section.detailsId
              ? fieldValuesWithDefaults[section.detailsId]
              : undefined
          }
          setFieldValues={setFieldValues}
        />
      )
    }
    case SectionType.FORM:
      return (
        <ConditionalFormSectionView
          config={section}
          ontologyFields={ontologyFields}
          virtualObject={fieldValuesWithDefaults}
          setVirtualObject={setFieldValues}
        />
      )
  }
}

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(
    () => ConditionalFormConfigSchema.safeParse(config).success,
    [config]
  )
}

export const Component: FC<
  LibraWorflowStepComponentProps<ConditionalFormConfig>
> = ({ config, setValidationResult }) => {
  const [isConfirmSaveVisible, setIsConfirmSaveVisible] = useState(false)

  const { ontologyFields } = useOntologyFields()
  const parsedConfig = useMemo(() => {
    return ConditionalFormConfigSchema.parse(config)
  }, [config])
  const { form, sections, lookups } = parsedConfig

  const {
    fieldValuesWithDefaults,
    initialFieldValues,
    setFieldValues,
    allVisibleSections,
    hasChanges,
    handleCancel,
    handleSave,
    isLoading,
  } = useWorkflowFieldValues({
    ontologyFields,
    sections,
    form,
  })

  const isStepComplete = useMemo(() => {
    return isConditionalFormStepComplete({
      config: parsedConfig,
      fieldValues: fieldValuesWithDefaults as Record<string, unknown>,
    })
  }, [parsedConfig, fieldValuesWithDefaults])

  useEffect(() => {
    setValidationResult(
      isStepComplete
        ? undefined
        : {
            status: 'Error',
            message: 'Step incomplete - please fill in all required fields',
          }
    )
  }, [isStepComplete, setValidationResult])

  return (
    <FormContainer>
      <StyledAlert
        title={hasChanges ? 'Save Changes?' : 'No Changes'}
        type={hasChanges ? 'warning' : 'success'}
        showIcon
        action={
          hasChanges && (
            <BannerActions>
              <CancelButton
                type='dashed'
                size='small'
                disabled={isLoading}
                onClick={handleCancel}>
                Cancel
              </CancelButton>
              <SaveButton
                type='primary'
                size='small'
                disabled={isLoading}
                onClick={() => setIsConfirmSaveVisible(true)}>
                Save
              </SaveButton>
            </BannerActions>
          )
        }
      />
      <Modal
        title='Confirm Save'
        loading={isLoading}
        open={isConfirmSaveVisible}
        onOk={async () => {
          await handleSave()
          setIsConfirmSaveVisible(false)
        }}
        onCancel={() => setIsConfirmSaveVisible(false)}
        okText='Save'
        cancelText='Cancel'
        confirmLoading={isLoading}>
        Are you sure you want to save these changes?
      </Modal>
      <FieldValueLookupProvider config={lookups}>
        {allVisibleSections.map((section) => (
          <SectionRenderer
            key={section.id}
            section={section}
            ontologyFields={ontologyFields}
            fieldValuesWithDefaults={fieldValuesWithDefaults}
            initialFieldValues={initialFieldValues}
            setFieldValues={setFieldValues}
          />
        ))}
      </FieldValueLookupProvider>
    </FormContainer>
  )
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const StyledAlert = styled(Alert)`
  min-height: 48px;

  .ant-alert-content {
    display: flex;
    align-items: center;
  }
`

const BannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const CancelButton = styled(Button)`
  background-color: ${cantileverTheme.colors.grayscale['500']} !important;
`

const SaveButton = styled(Button)`
  background-color: ${cantileverTheme.colors.OceanBlue} !important;
`
